import React, { useState } from 'react';
import { read as XLSXRead, utils as XLSXUtils } from 'xlsx';
import isEmail from 'validator/lib/isEmail';

import {
    Button,
    Dialog, DialogTitle, DialogContent, DialogActions
} from "@mui/material";

import SwipeableViews from 'react-swipeable-views-react-18-fix';

import WizardWrapper from './WizardWrapper';
import CreateClassForm from './CreateClassForm';
import AddStudentsMethodPicker from './AddStudentsMethodPicker';
import ClassWizardEnd from './ClassWizardEnd';
import ClassWizardCredentials from './ClassWizardCredentials';
import ConfirmDialog from './ConfirmDialog';

import { useAppState } from './AppStateContext';
import { classFieldErrors, isRowValid } from './helpers';

const StudentErrorMessage = ({ students, fails }) => {
    const notWithSchool = fails.filter(entry => entry.errors.includes("Not associated with your school"));
    const noSeatsLeft = fails.filter(entry => entry.errors.includes("No student licenses left"));
    const others = fails.filter(entry => !notWithSchool.includes(entry) && !noSeatsLeft.includes(entry));
    return (
        <div style={{margin: '0 0 16px', color: 'rgba(0, 0, 0, 0.54)', fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.43, letterSpacing: '0.01071em'}}>
            { notWithSchool.length > 0 && <>
                <p>The following email address{notWithSchool.length > 1 ? 'es' : ''} could not be added, because {notWithSchool.length > 1 ? 'they are' : 'it is'} not associated with your school:</p>
                <ul>
                    {notWithSchool.map(entry => (
                        <li key={entry.index}>{students[entry.index].email || students[entry.index].name}</li>
                    ))}
                </ul>
            </> }
            { noSeatsLeft.length > 0 && <>
                <p>The following student{noSeatsLeft.length > 1 ? 's' : ''} could not be added, because there are not enough student licenses left:</p>
                <ul>
                    {noSeatsLeft.map(entry => (
                        <li key={entry.index}>{students[entry.index].email || students[entry.index].name}</li>
                    ))}
                </ul>
            </> }
            { others.length > 0 && <>
                <p>The following student{others.length > 1 ? 's' : ''} could not be added:</p>
                <ul>
                    {others.map(entry => (
                        <li key={entry.index}>{students[entry.index].email || students[entry.index].name} ({entry.errors.join('; ')})</li>
                    ))}
                </ul>
            </> }
            <p>Press Ok to remove {fails.length > 1 ? 'these students' : 'this student'}. Then press Finish to try again.</p>
        </div>
    );
};

// An explicit marker in a spreadsheet's email column meaning "this student
// has no email address — generate an account". Unlike a blank cell, it is a
// deliberate statement, so the row can be imported pre-marked.
const isNoEmailMarker = (value) => ['none', '-', 'no email'].includes(value.toLowerCase());

const NewClassWizard = ({ classes, open, onClose }) => {
    const { apiPost } = useAppState();

    const [activeStep, setActiveStep] = useState(0);
    const defaultUserInput = { name: '', level: '', term: '' };
    const [userInput, setUserInput] = useState(defaultUserInput);
    const [nameError, setNameError] = useState(false);
    const [addMethod, setAddMethod] = useState(null);
    const [errorMessage, setErrorMessage] = useState("");
    const [openErrorDialog, setOpenErrorDialog] = useState(false);
    const [students, setStudents] = useState([]);
    const [failedStudents, setFailedStudents] = useState([]);
    const [createdStudents, setCreatedStudents] = useState([]);
    const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
    const [isCreating, setIsCreating] = useState(false);

    const handleBack = () => {
        setStudents([]);
        setAddMethod(null);
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const triggerFileInput = () => {
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = '.xlsx, .xls, .csv, .ods';
        fileInput.onchange = handleFileChange;
        fileInput.click();
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];

        const reader = new FileReader();
        reader.onload = (e) => {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSXRead(data, { type: 'array' });

            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];

            // raw: false returns the formatted cell text, so numeric-looking
            // passwords keep their exact digits and leading zeros instead of
            // going through a number round trip.
            const rows = XLSXUtils.sheet_to_json(worksheet, {header: 1, raw: false});

            const students = [];
            let headersOrder = null;
            let colOneEmails = 0;
            let colTwoEmails = 0;
            let passwordHeaderSeen = false;
            const EMAIL_FIRST = 1;
            const NAME_FIRST = 2;
            for (const row of rows) {
                if (headersOrder) {
                    // After a recognized header row only the name cell is
                    // required. A "none" marker in the email cell imports the
                    // row pre-marked "No email"; a *blank* cell imports it as
                    // an incomplete (red) row instead — the teacher must mark
                    // it by hand, so stray text in the name column cannot
                    // silently become a generated account.
                    const col0 = row.length > 0 && row[0] != null ? String(row[0]).trim() : '';
                    const col1 = row.length > 1 && row[1] != null ? String(row[1]).trim() : '';
                    const col2 = row.length > 2 && row[2] != null ? String(row[2]).trim() : undefined;
                    const email = headersOrder === EMAIL_FIRST ? col0 : col1;
                    const name = headersOrder === NAME_FIRST ? col0 : col1;
                    if (!name) continue;
                    const password = passwordHeaderSeen ? col2 : undefined;
                    if (isNoEmailMarker(email)) {
                        students.push({ name, email: '', noEmail: true, valid: isRowValid({ name, noEmail: true }), password });
                    } else {
                        students.push({ name, email, valid: isRowValid({ name, email }), password });
                    }
                } else {
                    if (row.length < 2 || !row[0] || !row[1]) continue;
                    // Cells are not necessarily strings: numeric passwords or
                    // ID-like cells arrive as numbers, and .trim()/isEmail()
                    // throw on those.
                    const col0 = String(row[0]).trim();
                    const col1 = String(row[1]).trim();
                    const col2 = row.length > 2 && row[2] != null ? String(row[2]).trim() : undefined;
                    // Normalize away hyphens and spaces, so that variants like
                    // "E-mail address" or "E-Mail-Address" are also recognized.
                    const header0 = col0.toLowerCase().replace(/[-\s]/g, '');
                    const header1 = col1.toLowerCase().replace(/[-\s]/g, '');
                    if ((header0 === 'email' || header0 === 'emailaddress') && header1 === 'name') {
                        headersOrder = EMAIL_FIRST;
                    } else if ((header1 === 'email' || header1 === 'emailaddress') && header0 === 'name') {
                        headersOrder = NAME_FIRST;
                    }
                    if (col2 !== undefined && col2.toLowerCase() === 'password') {
                        passwordHeaderSeen = true;
                    }
                    colOneEmails += isEmail(col0) ? 1 : 0;
                    colTwoEmails += isEmail(col1) ? 1 : 0;
                }
            }
            if (!headersOrder && colOneEmails !== colTwoEmails) {
                headersOrder = colOneEmails > colTwoEmails ? EMAIL_FIRST: NAME_FIRST;
                let firstRow = true;
                for (const row of rows) {
                    const col0 = row.length > 0 && row[0] != null ? String(row[0]).trim() : '';
                    const col1 = row.length > 1 && row[1] != null ? String(row[1]).trim() : '';
                    const col2 = row.length > 2 && row[2] != null ? String(row[2]).trim() : undefined;
                    const email = headersOrder === EMAIL_FIRST ? col0 : col1;
                    const name = headersOrder === NAME_FIRST ? col0 : col1;
                    if (!name) continue;
                    if (firstRow) {
                        firstRow = false;
                        // Most likely a header row with unrecognized labels
                        // (e.g. translated ones): skip the first row if its
                        // email column cannot possibly be an address. An
                        // explicit no-email marker is data, not a header.
                        if (!email.includes('@') && !isNoEmailMarker(email)) continue;
                    }
                    const password = passwordHeaderSeen ? col2 : undefined;
                    if (isNoEmailMarker(email)) {
                        students.push({ name, email: '', noEmail: true, valid: isRowValid({ name, noEmail: true }), password });
                    } else {
                        students.push({ name, email, valid: isRowValid({ name, email }), password });
                    }
                }
            }

            if (students.length === 0 && !headersOrder) {
                // A single-column sheet: typically a bare list of names for
                // students without email addresses (or, rarely, a list of
                // addresses without names). Import every cell as an
                // incomplete row; the teacher completes them in the review
                // table, e.g. with the "No email" select-all.
                const cells = rows
                    .filter(row => row.length > 0 && row[0] != null && String(row[0]).trim() !== '')
                    .map(row => String(row[0]).trim());
                const multiColumn = rows.some(row => row.length > 1 && row[1] != null && String(row[1]).trim() !== '');
                if (!multiColumn && cells.length > 0) {
                    const emailCount = cells.filter(cell => isEmail(cell)).length;
                    const isEmailColumn = emailCount * 2 > cells.length;
                    let firstCell = true;
                    for (const cell of cells) {
                        if (firstCell) {
                            firstCell = false;
                            const header = cell.toLowerCase().replace(/[-\s]/g, '');
                            if (header === 'name' || header === 'email' || header === 'emailaddress') continue;
                        }
                        const student = isEmailColumn ? { name: '', email: cell } : { name: cell, email: '' };
                        students.push({ ...student, valid: isRowValid(student) });
                    }
                }
            }

            setStudents(students);
            setActiveStep((prevActiveStep) => prevActiveStep + 1);
        };

        reader.readAsArrayBuffer(file);
    };

    const handleCloseErrorDialog = () => {
        setOpenErrorDialog(false);
    };

    const handleRemoveFailedStudents = () => {
        setStudents(students => students.filter((_, index) => !failedStudents.some(entry => entry.index === index)));
        setFailedStudents([]);
        handleCloseErrorDialog();
    };

    const handleCreate = async () => {
        // A double click on Finish must not create the class (and its
        // generated accounts) twice.
        if (isCreating) return;
        setIsCreating(true);
        try {
            const lastStudentEmpty = students.length > 0 && students[students.length - 1].name === '' && students[students.length - 1].email === '';
            const filteredStudents = lastStudentEmpty ? students.slice(0, -1) : students;
            const payloadStudents = filteredStudents.map(({ name, email, password, noEmail }) =>
                noEmail ? { name, password, no_email: true } : { name, email, password });
            const response = await apiPost('classes', { ...userInput, students: payloadStudents } );
            const created = response.data.createdStudents || [];
            if (created.length > 0) {
                setCreatedStudents(created);
                setActiveStep(3);
            } else {
                handleClose(null, 'created');
            }
        } catch (error) {
            if (error.response?.status === 422 && Array.isArray(error.response.data?.failedStudents)) {
                // Service-level errors arrive as arrays of strings, but
                // validation failures arrive as MessageBag objects keyed by
                // field; flatten so StudentErrorMessage can treat both alike.
                const failed = error.response.data.failedStudents.map(entry => ({
                    ...entry,
                    errors: Array.isArray(entry.errors) ? entry.errors : Object.values(entry.errors).flat(),
                }));
                setStudents(students.map((student, index) => ({ ...student, valid: student.valid && !failed.some(entry => entry.index === index) })));
                setFailedStudents(failed);
                setOpenErrorDialog(true);
            } else if (error.response?.status === 422) {
                // Not a per-student failure but class-field validation: put
                // the error on its field and return to the step showing it.
                // Errors on fields without an error slot (level, term) show
                // as the step's general error text.
                const fieldErrors = classFieldErrors(error.response.data?.errors);
                setNameError(fieldErrors.name);
                setErrorMessage(fieldErrors.other || (fieldErrors.name ? ""
                    : error.response.data?.message || "An error occurred while trying to create the class."));
                setActiveStep(0);
            } else {
                setErrorMessage("An error occurred while trying to create the class.");
            }
        } finally {
            setIsCreating(false);
        }
    };

    const handleClose = (event, reason) => {
        if (createdStudents.length > 0 && reason !== 'created') {
            // The generated passwords cannot be shown again once the wizard
            // closes; catch accidental Escape presses and X clicks.
            setConfirmCloseOpen(true);
            return;
        }
        // Closing from the credentials step means the class already exists,
        // so the parent must refetch no matter how the wizard was dismissed.
        const created = reason === 'created' || createdStudents.length > 0;
        setConfirmCloseOpen(false);
        setUserInput(defaultUserInput);
        setNameError(false);
        setStudents([]);
        setActiveStep(0);
        setAddMethod(null);
        setErrorMessage("");
        setFailedStudents([]);
        setCreatedStudents([]);
        onClose(created);
    };

    // The student-table steps stay capped below the wrapper's own
    // viewport-relative bound, so the table scrolls before the dialog grows.
    const wrapperSx = activeStep >= 2 ? {
            maxHeight: 'min(690px, calc(100vh - 64px))',
        } : {};

    return (
        // No going back from the credentials step: the class already exists,
        // and handleBack would wipe the student list.
        <WizardWrapper open={open} onClose={handleClose} onBack={handleBack} canGoBack={activeStep > 0 && activeStep < 3} sx={wrapperSx}>
            <SwipeableViews index={activeStep}>
                <CreateClassForm
                    classes={classes}
                    userInput={userInput}
                    setUserInput={(input) => { setErrorMessage(""); setUserInput(input); }}
                    nameError={nameError}
                    setNameError={setNameError}
                    setActiveStep={setActiveStep}
                    errorMessage={errorMessage}
                />
                <AddStudentsMethodPicker
                    addMethod={addMethod}
                    setAddMethod={setAddMethod}
                    setActiveStep={setActiveStep}
                    triggerFileInput={triggerFileInput}
                />
                <ClassWizardEnd
                    addMethod={addMethod}
                    userInput={userInput}
                    students={students}
                    setStudents={setStudents}
                    handleCreate={handleCreate}
                    isCreating={isCreating}
                    errorMessage={errorMessage}
                />
                <ClassWizardCredentials
                    className={userInput.name}
                    createdStudents={createdStudents}
                    onDone={() => handleClose(null, 'created')}
                />
            </SwipeableViews>

            <Dialog
                open={openErrorDialog}
                onClose={handleCloseErrorDialog}
            >
                <DialogTitle>{"Error"}</DialogTitle>
                <DialogContent>
                    <StudentErrorMessage students={students} fails={failedStudents} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleRemoveFailedStudents} color="primary">
                        Ok
                    </Button>
                    <Button onClick={handleCloseErrorDialog} color="secondary">
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                open={confirmCloseOpen}
                title="Close without saving?"
                message="Make sure you have saved or shared the generated passwords — they cannot be shown again, only reset. Close anyway?"
                confirmLabel="Close"
                onConfirm={() => handleClose(null, 'created')}
                onCancel={() => setConfirmCloseOpen(false)}
            />
        </WizardWrapper>
    );
};

export default NewClassWizard;
